import { create } from 'zustand';

/**
 * Transient global UI state — flags that coordinate cross-tree chrome but
 * carry no domain data (kept out of audioStore so playback state stays pure).
 *
 * `catalogReaderOpen`: true while the in-place catalog reader (CatalogReaderView)
 * is mounted on /catalog/:slug. The reader is local state inside CatalogWorldRoute
 * (not a distinct route), so AppShell has no URL to match — it reads this flag to
 * suppress the global MiniPlayer while the reader's own top-mounted player is
 * showing, avoiding a double player. Cleared on reader close / navigation away
 * (CatalogReaderView's unmount), so the MiniPlayer reappears on the Folgen list
 * or the library with the catalog track still playing.
 *
 * `catalogWorldCombinedPlayerOpen` (Paket 3): true while the whole-world
 * Gesamt-Hörbuch CombinedAudioPlayer is mounted on /catalog/:slug — after every
 * Folge is unlocked AND the combine has resolved to `ready`. Same MiniPlayer
 * double-render avoidance as catalogReaderOpen, but state-driven (not route-
 * driven): the flag flips ONLY while the player is actually on screen, so a
 * user who is browsing Wissenswelten with an unrelated story playing keeps
 * their MiniPlayer controls until the combined player itself surfaces. Set +
 * cleared by CatalogWorldRoute's effect on the mount.
 */
interface UiState {
  catalogReaderOpen: boolean;
  setCatalogReaderOpen: (open: boolean) => void;
  catalogWorldCombinedPlayerOpen: boolean;
  setCatalogWorldCombinedPlayerOpen: (open: boolean) => void;
}

export const useUiStore = create<UiState>((set) => ({
  catalogReaderOpen: false,
  setCatalogReaderOpen: (open) => set({ catalogReaderOpen: open }),
  catalogWorldCombinedPlayerOpen: false,
  setCatalogWorldCombinedPlayerOpen: (open) => set({ catalogWorldCombinedPlayerOpen: open }),
}));
